import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Truck,
  FileText,
  Users,
  Wrench,
  History,
  Settings,
  LogOut,
  ClipboardList,
  Bell,
  BarChart3,
  ShieldCheck,
  TrendingUp,
  Gauge,
  Building2,
  UserCog,
  CalendarDays,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { ROLE_LABEL } from "@/lib/format";
import NotificationBell from "@/components/NotificationBell";
import Logo from "@/components/Logo";

export default function Layout({ children }) {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;

  const navItems = [
    { to: "/dashboard", label: "Inicio", icon: LayoutDashboard, roles: ["superadmin", "rentacar_admin", "workshop_admin", "client_user"] },
    { to: "/flota", label: "Flota", icon: Truck, roles: ["superadmin", "rentacar_admin", "workshop_admin", "client_user"] },
    { to: "/contratos", label: "Contratos", icon: FileText, roles: ["superadmin", "rentacar_admin", "client_user"] },
    { to: "/conductores", label: "Conductores", icon: Users, roles: ["superadmin", "rentacar_admin"] },
    { to: "/empresas", label: "Empresas", icon: Building2, roles: ["rentacar_admin"] },
    { to: "/usuarios", label: "Usuarios", icon: UserCog, roles: ["rentacar_admin", "workshop_admin", "client_user"] },
    { to: "/taller", label: "Órdenes Taller", icon: ClipboardList, roles: ["superadmin", "workshop_admin", "rentacar_admin", "client_user"] },
    { to: "/calendario", label: "Calendario", icon: CalendarDays, roles: ["workshop_admin", "superadmin"] },
    { to: "/agendar", label: "Agendar Taller", icon: CalendarDays, roles: ["rentacar_admin"] },
    { to: "/mis-reservas", label: "Mis Reservas", icon: CalendarDays, roles: ["rentacar_admin", "workshop_admin", "client_user", "superadmin"] },
    { to: "/historial", label: "Historial Clínico", icon: History, roles: ["superadmin", "workshop_admin", "rentacar_admin"] },
    { to: "/alertas", label: "Alertas Pro", icon: Bell, roles: ["superadmin", "rentacar_admin"] },
    { to: "/insights", label: "Insights", icon: TrendingUp, roles: ["superadmin", "rentacar_admin", "workshop_admin", "client_user"] },
    { to: "/mantenciones", label: "Mantenciones", icon: Gauge, roles: ["superadmin", "rentacar_admin", "workshop_admin"] },
    { to: "/reportes", label: "Reportes", icon: BarChart3, roles: ["superadmin", "rentacar_admin", "workshop_admin"] },
    { to: "/admin", label: "Panel SuperAdmin", icon: ShieldCheck, roles: ["superadmin"] },
    { to: "/configuracion", label: "Configuración", icon: Settings, roles: ["superadmin", "rentacar_admin", "workshop_admin", "client_user"] },
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      {/* Sidebar */}
      <aside className="w-full md:w-72 md:min-h-screen border-b md:border-b-0 md:border-r border-gray-200 bg-white flex md:flex-col">
        <div className="p-6 border-b border-gray-200 hidden md:block">
          <div className="flex items-center gap-3">
            <Logo size={44} />
            <div>
              <h1 className="font-display text-2xl tracking-tighter leading-none">VETA CAR</h1>
              <p className="veta-tag mt-1">Fleet & Workshop OS</p>
            </div>
          </div>
        </div>

        {/* Mobile header */}
        <div className="md:hidden p-4 border-b border-gray-200 flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <Logo size={32} />
            <span className="font-display text-xl">VETA CAR</span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              className="text-xs uppercase tracking-wider px-3 py-2 border border-gray-200 hover:bg-black hover:text-white transition-colors"
              data-testid="mobile-logout-btn"
            >
              Salir
            </button>
          </div>
        </div>

        <nav className="hidden md:flex flex-col flex-1 p-4 gap-1">
          {navItems
            .filter((it) => it.roles.includes(role))
            .map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`nav-${it.to.slice(1)}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 h-touch text-sm font-medium uppercase tracking-wider transition-colors border-l-4 ${
                    isActive
                      ? "border-blue-600 bg-gray-50 text-black"
                      : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-black"
                  }`
                }
              >
                <it.icon size={18} strokeWidth={2.5} />
                {it.label}
              </NavLink>
            ))}
        </nav>

        {/* Mobile nav scroll */}
        <nav className="md:hidden flex overflow-x-auto p-2 gap-2 border-b border-gray-200 w-full">
          {navItems
            .filter((it) => it.roles.includes(role))
            .map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`mnav-${it.to.slice(1)}`}
                className={({ isActive }) =>
                  `flex-shrink-0 flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider border ${
                    isActive ? "border-black bg-black text-white" : "border-gray-200 bg-white text-gray-700"
                  }`
                }
              >
                <it.icon size={14} strokeWidth={2.5} />
                {it.label}
              </NavLink>
            ))}
        </nav>

        <div className="hidden md:block p-4 border-t border-gray-200">
          <div className="border border-gray-200 p-4 bg-gray-50">
            <p className="veta-tag">Sesión</p>
            <p className="font-bold text-sm mt-1 text-black truncate">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            <p className="veta-tag mt-2">{ROLE_LABEL[role]}</p>
            {company && (
              <p className="text-xs font-mono mt-2 text-gray-700 truncate">{company.name}</p>
            )}
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              className="mt-3 w-full h-touch flex items-center justify-center gap-2 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-600 transition-colors active:scale-[0.98]"
              data-testid="logout-btn"
            >
              <LogOut size={16} strokeWidth={2.5} /> Cerrar Sesión
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Desktop top bar with NotificationBell */}
        <div className="hidden md:flex border-b border-gray-200 bg-white px-8 py-3 items-center justify-end sticky top-0 z-30">
          <NotificationBell />
        </div>
        <div className="max-w-[1400px] mx-auto p-4 md:p-8">{children}</div>
      </main>
    </div>
  );
}
